import { useEffect, useState, useCallback } from "react";
import { Plus, MessageCircle } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { socialApi } from "@/api/social";
import BottomNav from "@/components/shell/BottomNav";
import AppHeader from "@/components/shell/AppHeader";
import NotificationsCenter from "@/components/social/NotificationsCenter";
import ProfilePage from "@/components/profile/ProfilePage";
import MyDashboard from "@/components/dashboard/MyDashboard";
import AlixChat from "@/components/alix/AlixChat";
import DocReader from "@/components/dashboard/DocReader";
import ReelsFeed from "@/components/reels/ReelsFeed";
import ChatHub from "@/components/chat/ChatHub";
import GuidedEntrance from "@/components/shell/GuidedEntrance";
import GlobalFeed from "@/components/social/GlobalFeed";
import QuickActionDrawer from "@/components/shell/QuickActionDrawer";
import DesktopNavRail from "@/components/shell/DesktopNavRail";
import Gateway from "@/components/gateway/Gateway";
import { setDirectorPrompt } from "@/lib/directorPrefill";
import { useWorkspace } from "@/context/WorkspaceContext";
import DesktopEscape from "@/components/shell/DesktopEscape";
import useIsDesktop from "@/lib/useIsDesktop";
import VideoEditorWorkspace from "@/components/editor/VideoEditorWorkspace";
import MisteckMark from "@/components/brand/MisteckMark";

function BootLoader() {
  return (
    <div className="h-full w-full grid place-items-center bg-[#0A0A0A]" data-testid="boot-loader">
      <div className="flex flex-col items-center gap-5">
        <MisteckMark size={96} pulse glow data-testid="boot-loader-glyph" />
        <span className="font-semibold text-2xl text-white lowercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>misteck</span>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Loading your studio…</p>
      </div>
    </div>
  );
}

const SHELL_VIEWS = ["me", "feed", "command", "chat", "alix", "doc-reader", "reels"];

// Resolve the initial top-level view from the URL (?tab=) → sessionStorage →
// default. A live Mist studio deep-link (?studio=) forces 'feed' so the
// dashboard + MistSuite can re-hydrate the exact sub-view on hard refresh.
function initialShellView() {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get("tab");
  if (SHELL_VIEWS.includes(tab)) return tab;
  if (params.get("studio") || params.get("hub")) return "command";
  // Login lands straight on the user's last view (or the Home command deck).
  // The Guided Entrance welcome pop-up has been removed at the user's request.
  const saved = sessionStorage.getItem("mist_shell_view");
  return SHELL_VIEWS.includes(saved) ? saved : "command";
}

export default function AppShell() {
  const { authed, logout } = useSession();
  const { setActiveWorkspaceView, setShell } = useWorkspace();
  const isDesktop = useIsDesktop();
  const [view, setView] = useState(initialShellView);
  const [socialUnread, setSocialUnread] = useState(0);
  const [navHidden, setNavHidden] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatSurface, setChatSurface] = useState(!!window.__mistChatSurface);
  const [returnView, setReturnView] = useState(() => sessionStorage.getItem("mist_shell_return") || "feed");

  // Chat surfaces (ALIX / JIM) broadcast this so the floating "+" FAB is suppressed.
  useEffect(() => {
    setChatSurface(!!window.__mistChatSurface);
    const onChat = (e) => setChatSurface(!!(e.detail && e.detail.active));
    window.addEventListener("mist:chat-surface", onChat);
    return () => window.removeEventListener("mist:chat-surface", onChat);
  }, []);

  // Persist the active top-level view to the URL (?tab=) + sessionStorage so a
  // pull-to-refresh keeps the user pinned to the exact zone they were on.
  const persistView = useCallback((v) => {
    sessionStorage.setItem("mist_shell_view", v);
    const p = new URLSearchParams(window.location.search);
    p.set("tab", v);
    // The Command Centre (dashboard + Mist studios) owns the ?studio deep-link;
    // any other top-level tab clears it so studios don't reopen behind them.
    if (v !== "command") { p.delete("studio"); p.delete("view"); p.delete("hub"); }
    const qs = p.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, []);

  // ME opens the Profile page. The center (+) FAB opens the Command Centre.
  const changeView = (v) => {
    setNavHidden(false);
    setFabOpen(false);
    if (v === "reels") {
      const rv = view === "reels" ? returnView : view;
      setReturnView(rv);
      sessionStorage.setItem("mist_shell_return", rv);
    }
    setView(v);
    persistView(v);
    // DESKTOP dual-pane: switching a top-level zone always resets the 2/3 pane —
    // tell the persistent chassis deck to close any deep view (Admin/Jim/Games/
    // Civ/hubs) it has portalled open. Desktop-gated → mobile is unaffected.
    if (isDesktop) window.dispatchEvent(new CustomEvent("mist:desk-nav", { detail: { view: v } }));
    // Returning to the Home Deck must PURGE any studio/hub deep-link from the URL
    // so the 2/3 Control Deck reliably re-shows and no studio silently re-hydrates
    // (in-app pushState left ?studio=/?hub= behind, hiding the deck until refresh).
    if (v === "command") {
      const p = new URLSearchParams(window.location.search);
      p.delete("studio"); p.delete("hub"); p.delete("view");
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  };

  // Jump straight into the Mist Director film workspace (from the Feed MAKE REEL
  // macro). Deep-link ?studio=director so MyDashboard→MistSuite hydrate it.
  const goToDirector = () => {
    const p = new URLSearchParams(window.location.search);
    p.set("studio", "directormode");
    p.delete("view");
    p.delete("tab");
    p.set("dstab", "reels");
    window.history.pushState({ studio: "directormode" }, "", `${window.location.pathname}?${p}`);
    setReturnView("reels");
    sessionStorage.setItem("mist_shell_return", "reels");
    setNavHidden(false);
    setView("command");
    sessionStorage.setItem("mist_shell_view", "command");
  };

  // AI DIRECTOR (Phase 26) — ALIX routes a natural-language / voice directive
  // straight into the matching Mist studio, pre-loading the distilled brief.
  const goToDirective = useCallback((d) => {
    if (!d || !d.studio) return;
    setDirectorPrompt(d.prompt);
    const p = new URLSearchParams(window.location.search);
    p.set("tab", "command");
    p.delete("hub");
    p.set("studio", d.studio);
    if (d.view) p.set("view", d.view);
    else p.delete("view");
    window.history.pushState({ studio: d.studio }, "", `${window.location.pathname}?${p}`);
    setReturnView("alix");
    sessionStorage.setItem("mist_shell_return", "alix");
    setNavHidden(false);
    setView("command");
    sessionStorage.setItem("mist_shell_view", "command");
  }, []);

  // Guided Entrance gate → open a hub cleanly (mirrors the quick-action hub path).
  const openHub = useCallback((hubKey) => {
    sessionStorage.setItem("mist_gate_seen", "1");
    const p = new URLSearchParams(window.location.search);
    p.delete("studio"); p.delete("view"); p.delete("hub");
    p.set("hub", hubKey); p.set("tab", "command");
    window.history.pushState({}, "", `${window.location.pathname}?${p}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
    setNavHidden(false);
    setView("command");
    sessionStorage.setItem("mist_shell_view", "command");
  }, []);

  const exploreAll = useCallback(() => {
    sessionStorage.setItem("mist_gate_seen", "1");
    changeView("command");
  }, [changeView]);

  // Quick Action Drawer (opened by the + FAB): Command Centre, the 4 quick
  // tabs (Careers/Leisure/Wealth/Creation), and Logout.
  const handleQuickAction = (key) => {
    setFabOpen(false);
    if (key === "logout") { logout(); return; }
    if (key === "dashboard") { changeView("command"); return; }
    const target = {
      creation: { hub: "imagery_hub" }, careers: { hub: "career_hub" },
      leisure: { hub: "lifestyle_hub" }, wealth: { hub: "wealth_hub" },
      blueprint: { hub: "blueprint_design" }, editor: { studio: "editor" },
    }[key];
    const p = new URLSearchParams(window.location.search);
    p.delete("studio"); p.delete("view"); p.delete("hub");
    if (target?.studio) p.set("studio", target.studio);
    else if (target?.hub) p.set("hub", target.hub);
    p.set("tab", "command");
    window.history.pushState({}, "", `${window.location.pathname}?${p}`);
    // pushState does NOT fire popstate — emit one so MyDashboard's URL→hub sync
    // runs even when we're already on the 'command' view (no remount otherwise).
    window.dispatchEvent(new PopStateEvent("popstate"));
    setNavHidden(false);
    setView("command");
    sessionStorage.setItem("mist_shell_view", "command");
  };

  // Keep the view in sync with browser back/forward navigation.
  useEffect(() => {
    const onPop = () => {
      setNavHidden(false);
      const params = new URLSearchParams(window.location.search);
      const tab = params.get("tab");
      if (SHELL_VIEWS.includes(tab)) setView(tab);
      else if (params.get("studio") || params.get("hub")) setView("command");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // After a user-initiated login/signup/tier-switch, land on the Command Centre.
  // (Boot token-restore sets no flag, so a refresh keeps the persisted zone.)
  useEffect(() => {
    if (authed !== true) return;
    const land = sessionStorage.getItem("mist_login_land");
    if (land) {
      sessionStorage.removeItem("mist_login_land");
      if (SHELL_VIEWS.includes(land)) { setNavHidden(false); setView(land); persistView(land); }
    }
  }, [authed, persistView]);

  const refreshUnread = useCallback(async () => {
    try {
      const { unread } = await socialApi.notifications();
      setSocialUnread(unread || 0);
    } catch (e) { /* not authed yet or transient */ }
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    refreshUnread();
    const id = setInterval(refreshUnread, 25000);
    return () => clearInterval(id);
  }, [authed, refreshUnread]);

  // Mirror the current view into the URL/sessionStorage (covers mount + swaps).
  useEffect(() => { if (authed === true) persistView(view); }, [view, authed, persistView]);

  // DESKTOP SHELL (md:+) — drive the right-hand contextual pane ('CONTROL_DECK'
  // on Home) and expose a live handle so the permanent left nav rail can steer
  // the SAME view state without remounting the app (renders keep running).
  useEffect(() => {
    setActiveWorkspaceView(view === "command" ? "CONTROL_DECK" : view.toUpperCase());
  }, [view, setActiveWorkspaceView]);
  useEffect(() => {
    setShell({
      view, socialUnread, authed: authed === true,
      navigate: changeView, openCreate: () => { console.log("[DBG] openCreate called", new Error().stack); setFabOpen(true); }, logout,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, socialUnread, authed]);

  if (authed === null) return <BootLoader />;
  if (authed === false) return <Gateway />;

  // Guided Entrance gate — calm 3-workflow welcome shown once per session on login.
  if (view === "gate") return <GuidedEntrance onOpenHub={openHub} onExplore={exploreAll} />;

  // REELS COMPLIANCE — MOBILE: full-viewport immersive player, nav/header fully
  // unmounted. DESKTOP: docks into the 2/3 workspace pane (rendered below) while
  // the 1/3 chassis stays as the persistent navigator.
  if (view === "reels" && !isDesktop) return <ReelsFeed onBack={() => changeView(returnView)} onMakeReel={goToDirector} />;

  // On desktop the chassis chrome mirrors the Home Control Deck (command) so the
  // persistent navigator never shows a secondary-tab back bar over MyDashboard.
  const chassisView = isDesktop ? "command" : view;

  return (
    <div className="h-full w-full max-w-full overflow-hidden flex flex-col bg-cream relative" data-testid="app-shell">
      <AppHeader view={chassisView} onHome={changeView} unread={socialUnread} onBell={() => setNotifOpen(true)} />
      {/* Notification bell lives ONLY on the Profile page (rendered inside the
          header there). It is intentionally absent from every other view. */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {/* DESKTOP (lg:+): the 1/3 chassis is a PERSISTENT navigator — it always
            renders the Home Control Deck (MyDashboard). Every top-level zone is
            lifted into the 2/3 workspace pane below. MOBILE: unchanged — the
            active view renders inline in the chassis. */}
        {isDesktop ? (
          <MyDashboard onNavVisibility={setNavHidden} onNavigate={changeView} />
        ) : (
          <>
            {view === "me" && <ProfilePage onNavigate={changeView} onNavVisibility={setNavHidden} />}
            {view === "feed" && <GlobalFeed onBack={() => changeView("command")} onAddMedia={goToDirector} onOpenReels={() => changeView("reels")} />}
            {view === "command" && <MyDashboard onNavVisibility={setNavHidden} onNavigate={changeView} />}
            {view === "chat" && <ChatHub />}
            {view === "alix" && <AlixChat onClose={() => changeView(returnView || "feed")} onDirective={goToDirective} onOpenDocReader={() => changeView("doc-reader")} onOpenLedger={() => changeView("command")} />}
            {view === "doc-reader" && <DocReader onClose={() => changeView("alix")} />}
          </>
        )}
      </div>

      {/* DESKTOP 2/3 DYNAMIC WORKSPACE PANE — the active zone renders here,
          portalled out of the 1/3 chassis. DECK (command) clears this pane so the
          Control Deck (App.js) shows through. */}
      {isDesktop && view !== "command" && (
        <DesktopEscape z="z-[48]" testid="desktop-workspace-pane">
          {view === "me" && <ProfilePage onNavigate={changeView} onNavVisibility={setNavHidden} />}
          {view === "feed" && <GlobalFeed onBack={() => changeView("command")} onAddMedia={goToDirector} onOpenReels={() => changeView("reels")} />}
          {view === "chat" && <ChatHub />}
          {view === "alix" && <AlixChat onClose={() => changeView(returnView || "feed")} onDirective={goToDirective} onOpenDocReader={() => changeView("doc-reader")} onOpenLedger={() => changeView("command")} />}
          {view === "reels" && <ReelsFeed onBack={() => changeView(returnView)} onMakeReel={goToDirector} />}
          {view === "editor" && <VideoEditorWorkspace />}
          {view === "doc-reader" && <DocReader onClose={() => changeView("alix")} />}
        </DesktopEscape>
      )}

      <BottomNav
        view={view}
        setView={changeView}
        socialUnread={socialUnread}
        hidden={navHidden}
      />

      {/* CREATOR DISPATCH — central black-circle breakout with the signature
          high-contrast yellow FAB locked inside a thick white boundary.
          Suppressed on chat surfaces (ALIX / JIM). */}
      {!chatSurface && view !== "alix" && (
      <button
        data-testid="nav-fab"
        aria-label="Create"
        onClick={() => { console.log("[DBG] nav-fab toggle"); setFabOpen((o) => !o); }}
        className={[
          "absolute left-1/2 -translate-x-1/2 z-[70] w-16 h-16 rounded-full bg-charcoal grid place-items-center transition-all duration-300 md:hidden",
          navHidden ? "bottom-[-80px] opacity-0 pointer-events-none" : "bottom-[34px] opacity-100",
        ].join(" ")}
      >
        <span className="w-[52px] h-[52px] rounded-full bg-[#F2C94C] border-4 border-white grid place-items-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <Plus size={24} strokeWidth={3.4} className="text-black" />
        </span>
      </button>
      )}

      {/* Floating conversation bubble removed — CHAT is reachable via the nav tab. */}

      <QuickActionDrawer open={fabOpen} onClose={() => setFabOpen(false)} onAction={handleQuickAction} />

      {notifOpen && (
        <div className="absolute inset-0 z-[85] bg-cream" data-testid="notif-center-overlay">
          <NotificationsCenter
            onClose={() => { setNotifOpen(false); refreshUnread(); }}
            onNavigate={(v) => { setNotifOpen(false); refreshUnread(); changeView(v); }}
          />
        </div>
      )}
    </div>
  );
}
